import { useDeleteProductMutation, useGetProductQuery } from '@codezo/graphql';
import { ArrowLeft, Globe, Image as ImageIcon, Pencil, Plus, Ruler } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { EmptyState } from '../../components/ui/empty-state';
import { FilePreview, type FilePreviewItem } from '../../components/ui/file-preview';
import { ImageViewer } from '../../components/ui/image-viewer';
import { ProductImage } from '../../components/ui/product-image';
import { SectionCard } from '../../components/ui/section-card';
import { Tabs } from '../../components/ui/tabs';
import { useQueryParam } from '../../hooks/useQueryParam';
import { formatDate, formatMonthYear } from '../../lib/utils';
import { SpecificationsView, buildSpecificationRows } from '@codezo/product-specs';
import { EditProductModal, type EditProductTab } from './EditProductModal';
import { useAddToMyProducts } from './useAddToMyProducts';

/* ── Detail row ── */

function DetailRow({
	label,
	value,
	last,
}: {
	label: string;
	value: React.ReactNode;
	last?: boolean;
}) {
	return (
		<div
			className="flex items-center justify-between px-4 py-2.5 text-sm"
			style={!last ? { borderBottom: '0.5px solid var(--line-1)' } : undefined}
		>
			<span className="text-text-3">{label}</span>
			<span className="text-text-1">{value}</span>
		</div>
	);
}

/* ── Loading skeleton ── */

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-5 h-4 w-20 animate-pulse rounded bg-surface-fill" />
			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
				<div className="aspect-square animate-pulse rounded-[var(--r-3)] bg-surface-fill" />
				<div className="space-y-4">
					<div className="h-8 w-3/4 animate-pulse rounded bg-surface-fill" />
					<div className="h-4 w-1/2 animate-pulse rounded bg-surface-fill" />
					<div className="h-32 animate-pulse rounded bg-surface-fill" />
				</div>
			</div>
		</div>
	);
}

/* ════════════════════════ PRODUCT DETAIL PAGE ════════════════════════ */

export function ProductDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [tab, setTab] = useQueryParam('tab', 'overview');
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [editTab, setEditTab] = useState<EditProductTab>('details');

	const openEdit = (nextTab: EditProductTab) => {
		setEditTab(nextTab);
		setEditOpen(true);
	};
	const [deleteOpen, setDeleteOpen] = useState(false);
	const { requestAdd, addDialog } = useAddToMyProducts();

	const { data, loading, error, refetch } = useGetProductQuery({
		variables: { id: id! },
		fetchPolicy: 'no-cache',
		skip: !id,
	});

	const [deleteProduct, { loading: deleteLoading }] = useDeleteProductMutation();

	const product = data?.getProduct;

	const handleDelete = async () => {
		if (!product) return;
		try {
			await deleteProduct({ variables: { input: { id: product.id } } });
			toast.success(`${product.name} deleted`);
			setDeleteOpen(false);
			navigate('/products');
		} catch {
			toast.error('Failed to delete product');
		}
	};

	// NOT_FOUND: not ours and not on the community page (e.g. unpublished since the link was
	// shared). Anything else (network, server) is a failed load and can be retried.
	if (error) {
		const notFound = error.graphQLErrors.some((e) => e.extensions?.code === 'NOT_FOUND');
		return (
			<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<EmptyState
					title={
						notFound
							? 'This product is no longer available'
							: 'Could not load this product'
					}
					description={
						notFound
							? 'It may have been removed from the community.'
							: 'Something went wrong while loading it. Please try again.'
					}
				>
					{notFound ? (
						<Button variant="outline" size="sm" onClick={() => navigate('/products')}>
							Back to products
						</Button>
					) : (
						<Button variant="outline" size="sm" onClick={() => refetch()}>
							Try again
						</Button>
					)}
				</EmptyState>
			</div>
		);
	}

	if (loading || !product) {
		return <DetailSkeleton />;
	}

	// The backend decides what this viewer may do. A product with no organization can only reach a
	// client as a community product: read-only here, with "Add to my products" as its one action.
	const { canEdit, canDelete, canDuplicate } = product.availableActions;
	const isCommunityProduct = !product.organizationId;

	const previewFiles: FilePreviewItem[] = (product.images ?? [])
		.filter((img) => img.url)
		.map((img) => ({
			id: img.id,
			url: img.url!,
			name: img.name,
			mimeType: img.mimeType || 'image/jpeg',
		}));

	const specRows = buildSpecificationRows({
		specifications: product.specifications,
		dimensions: product.dimensions,
		weight: product.weight,
	});

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			{/* ── Back ── */}
			<button
				onClick={() =>
					navigate(isCommunityProduct ? '/products?tab=community' : '/products')
				}
				className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				{isCommunityProduct ? 'Community' : 'Products'}
			</button>

			{/* ── Main layout: images left, info right ── */}
			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
				{/* Left: Image gallery */}
				<div>
					<ImageViewer images={product.images ?? []} />
				</div>

				{/* Right: Product info */}
				<div>
					{/* Header */}
					<div className="mb-6 flex items-start justify-between gap-4">
						<div>
							<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
								{product.name}
							</h1>
							<div className="flex flex-wrap items-center gap-3 text-base text-text-3">
								{product.sku && (
									<span className="font-mono text-text-4">{product.sku}</span>
								)}
								{product.createdAt && (
									<>
										<span className="text-text-4">·</span>
										<span>Created {formatMonthYear(product.createdAt)}</span>
									</>
								)}
							</div>
						</div>
						<div className="flex shrink-0 gap-2">
							{isCommunityProduct && canDuplicate && (
								<Button size="sm" onClick={() => requestAdd(product)}>
									<Plus size={14} data-icon="inline-start" />
									Add to my products
								</Button>
							)}
							{canEdit && (
								<Button
									variant="outline"
									size="sm"
									onClick={() => openEdit('details')}
								>
									<Pencil size={14} data-icon="inline-start" />
									Edit
								</Button>
							)}
							{canDelete && (
								<Button
									variant="destructive"
									size="sm"
									onClick={() => setDeleteOpen(true)}
								>
									Delete
								</Button>
							)}
						</div>
					</div>

					{/* Tabs */}
					<div className="mb-6">
						<Tabs
							value={tab}
							onChange={setTab}
							items={[
								{ value: 'overview', label: 'Overview' },
								{
									value: 'images',
									label: 'Images',
									count: product.images?.length ?? 0,
								},
							]}
						/>
					</div>

					{/* ── Overview tab ── */}
					{tab === 'overview' && (
						<div className="flex flex-col gap-4">
							{/* Description */}
							<SectionCard title="Description">
								<div className="px-4 py-3 text-base leading-relaxed text-text-2">
									{product.description || (
										<span className="text-text-4">
											No description added yet.
										</span>
									)}
								</div>
							</SectionCard>

							{/* Details */}
							<SectionCard title="Details">
								<div className="flex flex-col">
									<DetailRow label="SKU" value={product.sku || '—'} />
									{product.sourceProductId && (
										<DetailRow
											label="Source"
											value={
												<span className="inline-flex items-center gap-1.5">
													<Globe size={12} className="text-text-3" />
													Added from Community
												</span>
											}
										/>
									)}
									{product.brand && (
										<DetailRow label="Brand" value={product.brand.name} />
									)}
									<DetailRow
										label="Created"
										value={
											product.createdAt ? formatDate(product.createdAt) : '—'
										}
									/>
									<DetailRow
										label="Updated"
										value={
											product.updatedAt ? formatDate(product.updatedAt) : '—'
										}
										last
									/>
								</div>
							</SectionCard>

							{/* Specifications */}
							<SectionCard
								title="Specifications"
								action={
									canEdit && specRows.length > 0 ? (
										<button onClick={() => openEdit('specifications')}>
											<Pencil size={12} />
											Edit
										</button>
									) : undefined
								}
							>
								{specRows.length > 0 ? (
									<SpecificationsView rows={specRows} />
								) : (
									<div className="flex flex-col items-center gap-3 py-10 text-center">
										<div className="flex h-9 w-9 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
											<Ruler size={16} />
										</div>
										<div className="text-sm text-text-3">
											No specifications added yet
										</div>
										{canEdit && (
											<Button
												variant="outline"
												size="sm"
												onClick={() => openEdit('specifications')}
											>
												Add specifications
											</Button>
										)}
									</div>
								)}
							</SectionCard>

							{/* Design file */}
							{product.studioFile && (
								<SectionCard title="Design file">
									<div className="flex items-center gap-3 px-4 py-3">
										<div className="flex h-9 w-9 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill">
											<ImageIcon size={16} className="text-text-3" />
										</div>
										<div className="min-w-0 flex-1">
											<div className="text-base font-medium text-text-1">
												{product.studioFile.name}
											</div>
											{product.studioFile.currentVersion && (
												<div className="text-xs text-text-4">
													Version{' '}
													{
														product.studioFile.currentVersion
															.versionNumber
													}
												</div>
											)}
										</div>
									</div>
								</SectionCard>
							)}
						</div>
					)}

					{/* ── Images tab ── */}
					{tab === 'images' && (
						<SectionCard title={`All images (${product.images?.length ?? 0})`}>
							{!product.images || product.images.length === 0 ? (
								<div className="flex flex-col items-center gap-3 py-14 text-center">
									<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
										<ImageIcon size={20} />
									</div>
									<div className="text-base text-text-2">No images</div>
									<div className="text-sm text-text-4">
										Upload images to showcase this product.
									</div>
								</div>
							) : (
								<div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
									{product.images.map((img) => (
										<div
											key={img.id}
											onClick={() => {
												const idx = previewFiles.findIndex(
													(f) => f.id === img.id
												);
												if (idx >= 0) setPreviewIndex(idx);
											}}
											className="group relative cursor-pointer overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-fill transition-colors hover:border-line-2"
										>
											{img.url ? (
												<ProductImage
													image={img}
													className="aspect-square w-full"
												/>
											) : (
												<div className="flex aspect-square items-center justify-center">
													<ImageIcon size={20} className="text-text-4" />
												</div>
											)}
											{img.isPrimary && (
												<span className="absolute top-2 left-2 rounded-full bg-accent px-2 py-0.5 text-2xs font-medium text-accent-fg">
													Primary
												</span>
											)}
											<div className="px-2.5 py-2">
												<div className="truncate text-xs text-text-2">
													{img.name}
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</SectionCard>
					)}
				</div>
			</div>

			{previewIndex !== null && (
				<FilePreview
					files={previewFiles}
					initialIndex={previewIndex}
					onClose={() => setPreviewIndex(null)}
				/>
			)}

			<EditProductModal
				open={editOpen}
				onOpenChange={setEditOpen}
				initialTab={editTab}
				productId={product.id}
				onSaved={refetch}
			/>

			{addDialog}

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title="Delete product"
				description={`Are you sure you want to delete "${product.name}"? This action cannot be undone.`}
				confirmLabel="Delete"
				loading={deleteLoading}
				onConfirm={handleDelete}
			/>
		</div>
	);
}
